import React, { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { subDays, startOfToday, endOfToday } from 'date-fns';
import { useParams } from 'react-router-dom';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

function TemperatureAnalysis() {
  const { androidId } = useParams();
  const [chartData, setChartData] = useState(null);
  const [filterWeeks, setFilterWeeks] = useState(0);
  const [chartDate, setChartDate] = useState('');
  const [noData, setNoData] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        setNoData(false); // Reset noData state on new fetch
        setError('');
        setChartData(null);
        const serverUrl = process.env.REACT_APP_API_SERVER_URL;
        const url = `${serverUrl}/maiApi/device/${androidId}/temperature-history`;
        const res = await fetch(url);
        if (!res.ok) {
         // const text = await res.text();
          //throw new Error(`HTTP ${res.status}: ${text.slice(0, 200)}`);
          throw new Error(`HTTP ${res.status}: Failed to fetch`);
        }
        const data = await res.json();
        if (!data || data.length === 0)  {
          setNoData(true);
           return; }

        const now = new Date();
        let filteredData = [];

        if (filterWeeks === 0) {
          const todayStr = now.toISOString().split('T')[0];
          filteredData = data.filter(row => {
            const rowDateStr = new Date(row.last_updated_time).toISOString().split('T')[0];
            return rowDateStr === todayStr;
          });
        } else {
          const startDate = subDays(now, filterWeeks * 7);
          filteredData = data.filter(row => {
            const rowDate = new Date(row.last_updated_time);
            return rowDate >= startDate && rowDate <= now;
          });
        }

        //if (filteredData.length === 0) return;

        const dataPoints = filteredData.map(row => ({
          x: new Date(row.last_updated_time),
          y: row.temperature === null ? null : parseFloat(row.temperature)
        }))
        .filter(point => !isNaN(point.y));
        
         if (dataPoints.length === 0) {
          setNoData(true);
          setChartData({});
          return;
        }
        /*let ticks = [];
        if (filterWeeks === 0) {
          const start = new Date(now.toISOString().split('T')[0] + 'T00:00:00');
          for (let i = 0; i < 24; i += 2) {
            const tick = new Date(start);
            tick.setHours(i);
            ticks.push(tick);
          }
        }
*/
        setChartDate(
          filterWeeks === 0
            ? startOfToday().toLocaleDateString()
            : `Past ${filterWeeks} week${filterWeeks > 1 ? 's' : ''}`
        );

        setChartData({
          //labels: filterWeeks === 0 ? ticks : filteredData.map(row => new Date(row.date_time)),
          datasets: [
            {
              label: 'Temperature (°F)',
              data: dataPoints,
              fill: false,
              borderColor: 'orange',
              tension: 0.1
            }
          ]
        });
      } catch (error) {
        console.error("Failed to fetch temperature data:", error);
        setError("Failed to load temperature data");
        setChartData({});
      }
    })();
  }, [androidId, filterWeeks]);

  /*const tickOptions = filterWeeks === 0
    ? {
        source: 'labels',
        autoSkip: false,
        callback: val => {
          const date = new Date(val);
          return date.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          });
        }
      }
    : {
        autoSkip: true,
        maxTicksLimit: 12,
        maxRotation: 45,
        minRotation: 45
      };
*/
  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          boxWidth: 20,
          font: {
            size: 14
          }
        }
      },
      title: {
        display: true,
        text: 'Temperature Analysis',
        font: {
          size: 18
        }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
      }
    },
    scales: {
      x: {
        type: 'time',
        time: {
          unit: filterWeeks === 0 ? 'hour' : 'day',
          displayFormats: {
            hour: 'hh:mm a',
            day: 'MMM dd'
          },
          tooltipFormat: filterWeeks === 0 ? 'hh:mm a' : 'MMM dd',
        },
        min: filterWeeks === 0 ? startOfToday().toISOString() : undefined,
        max: filterWeeks === 0 ? endOfToday().toISOString() : undefined,
        title: {
          display: true,
          text: 'Time (HH:mm AM/PM)'
        },
        // C8 FIX: The weekly ticks object was missing its closing brace, which
        // caused the y-axis config to be swallowed inside the x-axis object.
        ticks: filterWeeks === 0
          ? {
              source: 'auto',
              autoSkip: true,
              maxTicksLimit: 12,
            }
          : {
              autoSkip: true,
              maxTicksLimit: 10,
              maxRotation: 45,
              minRotation: 45,
            },
      },
      y: {
        title: {
          display: true,
          text: 'Temperature (°F)'
        },
        suggestedMin: 0,
        suggestedMax: 40,
        ticks: {
          stepSize: 10
        }
      }
    }
  };

  if (!chartData) return <div>Loading chart...</div>;

  return (
    <div style={{ width: '80%', margin: 'auto' }}>
      {/* Filter Buttons */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px', marginBottom: '15px' }}>
        {[0, 1, 4, 8].map(value => (
          <button
            key={value}
            onClick={() => setFilterWeeks(value)}
            style={{
              backgroundColor: filterWeeks === value ? 'darkorange' : '#fdf2e9',
              color: filterWeeks === value ? 'white' : 'black',
              padding: '8px 16px',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 'bold',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.2)',
              transition: 'background-color 0.3s ease',
            }}
          >
            {value === 0 ? 'Today' : `${value} Week${value > 1 ? 's' : ''}`}
          </button>
        ))}
      </div>

      {chartDate && (
        <p style={{ textAlign: 'right', fontSize: '16px', marginBottom: '10px' }}>
          <strong>{filterWeeks === 0 ? 'Date' : 'Range'}:</strong> {chartDate}
        </p>
      )}
    {error ? (
        <div style={{ textAlign: 'center', padding: '50px', border: '1px solid #ddd', borderRadius: '8px', color: '#b91c1c' }}>
          {error}
        </div>
      ) : noData ? (
        <div style={{ textAlign: 'center', padding: '50px', border: '1px solid #ddd', borderRadius: '8px' }}>
          No temperature data available for the selected period.
        </div>
      ) : (
      <Line data={chartData} options={options} /> )}
    </div>
  );
}

export default TemperatureAnalysis;

